import {
  Autocomplete as MuiAutocomplete,
  AutocompleteChangeDetails,
  AutocompleteChangeReason,
  AutocompleteInputChangeReason,
  AutocompleteProps as MuiAutocompleteProps,
  AutocompleteValue,
  CircularProgress,
} from "@mui/material";
import { debounce, uniqBy } from "lodash";
import {
  Key,
  ReactElement,
  ReactNode,
  Ref,
  SyntheticEvent,
  UIEvent,
  useCallback,
  useMemo,
} from "react";
import { compare, Option } from "../../../types/option";
import { TextField } from "../TextField/TextField";

type AutocompleteChangeOption<T extends Key, TMeta, FreeSolo extends boolean | undefined> =
  | Option<T, TMeta>
  | (FreeSolo extends true ? string : never);

export type AutocompleteProps<
  T extends Key = Key,
  TMeta = unknown,
  Multiple extends boolean | undefined = boolean,
  DisableClearable extends boolean | undefined = boolean,
  FreeSolo extends boolean | undefined = false,
> = Omit<
  MuiAutocompleteProps<Option<T, TMeta>, Multiple, DisableClearable, FreeSolo>,
  "renderInput" | "onChange" | "onInputChange" | "options" | "value" | "isOptionEqualToValue"
> & {
  name?: string;
  label?: string;
  placeholder?: string;
  alwaysShowPlaceholder?: boolean;
  helperText?: ReactNode;
  cornerHelperText?: ReactNode;
  tooltip?: ReactNode;
  error?: boolean | string;
  required?: boolean;
  inputDebounceTime?: number;
  inputRef?: Ref<unknown>;
  controlRef?: Ref<unknown>;
  options: readonly Option<T, TMeta>[];
  multiple?: Multiple;
  value?: AutocompleteValue<Option<T, TMeta>, Multiple, DisableClearable, FreeSolo>;
  variant?: "outlined" | "filled";
  onScrollEnd?: () => void | Promise<void>;
  onInputChange?: (
    query: string,
    reason: AutocompleteInputChangeReason,
    event: SyntheticEvent<Element, Event>,
  ) => void | Promise<void>;
  onChange?: (
    value: AutocompleteValue<Option<T, TMeta>, Multiple, DisableClearable, FreeSolo>,
    {
      event,
      reason,
      details,
    }: {
      event: SyntheticEvent<Element, Event>;
      reason: AutocompleteChangeReason;
      details?: AutocompleteChangeDetails<AutocompleteChangeOption<T, TMeta, FreeSolo>>;
    },
  ) => void;
};

export const Autocomplete = <
  T extends Key = Key,
  TMeta = unknown,
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = false,
>({
  name,
  label,
  placeholder,
  alwaysShowPlaceholder,
  helperText,
  cornerHelperText,
  tooltip,
  error,
  readOnly,
  required,
  options,
  value,
  multiple,
  size,
  variant,
  loading,
  inputDebounceTime,
  inputRef,
  controlRef,
  onChange,
  onInputChange,
  ChipProps,
  ListboxProps,
  sx,
  onScrollEnd,
  ...props
}: AutocompleteProps<T, TMeta, Multiple, DisableClearable, FreeSolo>): ReactElement => {
  const effectivePlaceholder =
    !alwaysShowPlaceholder && Array.isArray(value) && value.length ? undefined : placeholder;

  const onScroll = useCallback(
    async (event: UIEvent<HTMLUListElement>) => {
      ListboxProps?.onScroll?.(event);
      const target = event.currentTarget;

      const { scrollTop, clientHeight, scrollHeight } = target;
      if (scrollTop + clientHeight >= scrollHeight) {
        const result = onScrollEnd?.();

        if (result instanceof Promise) {
          await result;
        }

        // There is an issue since v5 where the after load,  scroll  is sent to top
        // fix should be incoming soon https://github.com/mui-org/material-ui/issues/18450#issuecomment-983086685
        target.scrollTo({
          top: scrollTop,
          behavior: "auto",
        });
      }
    },
    [ListboxProps, onScrollEnd],
  );

  const listBoxProps = {
    ...ListboxProps,
    ...(onScrollEnd && { onScroll }),
    "data-testid": `${name || "sr"}-listbox`,
  };

  const handleChange = useCallback(
    (
      event: SyntheticEvent<Element, Event>,
      value: AutocompleteValue<Option<T, TMeta>, Multiple, DisableClearable, FreeSolo>,
      reason: AutocompleteChangeReason,
      details?: AutocompleteChangeDetails<AutocompleteChangeOption<T, TMeta, FreeSolo>>,
    ) => {
      onChange?.(value, { event, reason, details });
    },
    [onChange],
  );

  const debounceInputChange = useMemo(
    () => onInputChange && debounce(onInputChange, inputDebounceTime),
    [onInputChange, inputDebounceTime],
  );

  const handleInputChange = useCallback(
    (
      event: SyntheticEvent<Element, Event>,
      value: string,
      reason: AutocompleteInputChangeReason,
    ) => {
      const fn = inputDebounceTime ? debounceInputChange : onInputChange;
      fn?.(value, reason, event);
    },
    [inputDebounceTime, onInputChange, debounceInputChange],
  );

  const effectiveOptions = useMemo(() => {
    if (!value || (Array.isArray(value) && !value.length)) {
      return options;
    }

    const include = (!Array.isArray(value) ? [value] : value).filter(
      (candidate): candidate is Option<T, TMeta> =>
        typeof candidate === "object" && candidate !== null && "value" in candidate,
    );

    return uniqBy(include.concat(options), (v: Option<T, TMeta>) => v.value);
  }, [value, options]);
  return (
    <MuiAutocomplete
      {...props}
      readOnly={readOnly}
      loading={loading}
      value={value as AutocompleteValue<Option<T, TMeta>, Multiple, DisableClearable, FreeSolo>}
      multiple={multiple}
      size={size}
      options={effectiveOptions}
      isOptionEqualToValue={compare}
      onChange={!readOnly ? handleChange : undefined}
      onInputChange={!readOnly ? handleInputChange : undefined}
      ChipProps={{ ...ChipProps, size: "small", sx: { px: { xs: 1 } } }}
      ListboxProps={listBoxProps}
      sx={[
        multiple && {
          "&& .MuiAutocomplete-inputRoot": {
            height: "auto",
            py: 0.5,
          },
        },
        ...(Array.isArray(sx) ? sx : [sx]),
      ]}
      renderInput={(params) => (
        <TextField
          {...params}
          variant={variant}
          key={name || ""}
          name={name || ""}
          label={label}
          placeholder={effectivePlaceholder}
          helperText={helperText}
          cornerHelperText={cornerHelperText}
          tooltip={tooltip}
          error={error}
          readOnly={readOnly}
          required={required}
          inputRef={inputRef}
          controlRef={controlRef}
          size={size}
          inputProps={{
            ...params.inputProps,
            "data-testid": `${name || "sr"}-select-input`,
            sx: {
              boxSizing: "content-box",
              "&&.MuiAutocomplete-input": {
                minWidth: "0",
              },
            },
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: !readOnly ? (
              <>
                {loading ? (
                  <CircularProgress
                    color="inherit"
                    aria-label="loading"
                    size={20}
                    sx={{
                      // @ts-ignore - TODO: remove this once https://github.com/mui-org/material-ui/issues/28465 is resolved.
                      marginRight:
                        params.InputProps.startAdornment || params.inputProps.value ? 0 : 4,
                    }}
                  />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ) : undefined,
          }}
        />
      )}
    />
  );
};
